<template>
    <v-container fluid>
        <v-row justify="center" align="center" v-if="loading">
            <div class="text-center">
                <div>
                    <v-progress-circular class="my-4" :size="50" width="5" color="primary"
                        indeterminate></v-progress-circular>
                </div>
                <p class="display-1">{{ $t("Loading") }}...</p>
            </div>
        </v-row>
        <v-row dense v-if="!loading">
            <v-col md="6" sm="12">
                <v-card>
                    <v-card-title>
                        <v-icon large left> mdi-cogs </v-icon>
                        <span class="title font-weight-light">{{ $t('General') }}</span>
                    </v-card-title>
                    <v-card-text>
                        <v-text-field :label="$t('ParamWebsiteURL')" v-model="parameter.WebsiteURL"></v-text-field>
                        <v-text-field :label="$t('ParamWebApiURL')" v-model="parameter.WebApiURL"></v-text-field>
                        <v-text-field :label="$t('ParamAIModelsAPIURL')"
                            v-model="parameter.AIRecognitionAPIURL"></v-text-field>
                        <v-text-field :label="$t('ParamDocSeriesAIApiURL')"
                            v-model="parameter.AutorecognitionApiURL"></v-text-field>
                        <v-text-field :label="$t('ParamOCRAPIURL')" v-model="parameter.OCRAPIURL"></v-text-field>
                        <v-text-field :label="$t('JobsExecutionTimeAlert')"
                            v-model="parameter.JobsExecutionTimeAlert"></v-text-field>
                        <v-text-field :label="$t('JobsExecutionTimeDataManagement')"
                            v-model="parameter.JobsExecutionTimeDataManagement"></v-text-field>
                        <v-text-field :label="$t('JobsExecutionTimeDelegations')"
                            v-model="parameter.JobsExecutionTimeDelegations"></v-text-field>
                        <v-text-field :label="$t('JobsExecutionTimeDocumentLifeCycle')"
                            v-model="parameter.JobsExecutionTimeDocumentLifeCycle"></v-text-field>
                        <v-text-field :label="$t('JobsExecutionTimeConnectedServices')"
                            v-model="parameter.JobsExecutionTimeConnectedServices"></v-text-field>
                        <v-textarea :label="$t('ParamSupportedFileFormats')" v-model="parameter.SupportedFileFormats">
                            <template v-slot:append>
                                <v-tooltip bottom>
                                    <template v-slot:activator="{ on }">
                                        <v-icon v-on="on"> mdi-help-circle-outline </v-icon>
                                    </template>
                                    {{ $t('ParamSupportedFileFormatsInfo') }}
                                </v-tooltip>
                            </template>
                        </v-textarea>
                    </v-card-text>
                    <v-card-actions>
                        <v-btn color="warning" @click="resetCache">{{ $t('Site.Parameters.ResetCache') }}</v-btn>
                    </v-card-actions>
                </v-card>

                <v-card>
                    <v-card-title>
                        <v-icon large left> mdi-brain </v-icon>
                        <span class="title font-weight-light">{{ $t('Site.Parameters.AIDefaultModel') }}</span>
                    </v-card-title>
                    <v-card-text>
                        <v-text-field v-if="parameter.AIDefaultModel" :label="$t('Model')"
                            v-model="parameter.AIDefaultModel"></v-text-field>
                    </v-card-text>
                </v-card>
            </v-col>
            <v-col md="6" sm="12">
                <v-card>
                    <v-card-title>
                        <v-icon large left> mdi-server-security </v-icon>
                        <v-switch v-model="parameter.ActiveDirectoryAuthEnabled" color="green"
                            :label="$t('ParamActiveDirectory')"></v-switch>
                    </v-card-title>
                    <v-card-text v-if="parameter.ActiveDirectoryAuthEnabled">
                        <v-switch v-model="parameter.AzureUseActiveDirectory" color="green"
                            :label="$t('UseAzure')"></v-switch>
                        <v-select v-if="parameter.AzureUseActiveDirectory" :label="$t('EntraIdDefaultOrganization')"
                            v-model="parameter.MicrosoftEntraIDDefaultOrganizationId" :items="organizations"
                            item-text="text" item-value="id"></v-select>
                        <v-text-field v-if="parameter.AzureUseActiveDirectory" :label="$t('ClientID')"
                            v-model="parameter.MicrosoftEntraClientId"></v-text-field>
                        <v-text-field v-if="parameter.AzureUseActiveDirectory" :label="$t('ClientSecret')"
                            v-model="parameter.MicrosoftEntraClientSecret"></v-text-field>
                        <v-text-field v-if="!parameter.AzureUseActiveDirectory"
                            v-model="parameter.ActiveDirectoryServerPath" :label="$t('Server')">
                            <template v-slot:append v-if="!parameter.AzureUseActiveDirectory">
                                <v-tooltip bottom>
                                    <template v-slot:activator="{ on }">
                                        <v-icon v-on="on"> mdi-help-circle-outline </v-icon>
                                    </template>
                                    LDAP://srv-ecm.adserver.local
                                </v-tooltip>
                            </template>
                        </v-text-field>
                        <v-text-field v-if="!parameter.AzureUseActiveDirectory"
                            :label="$t('ParamActiveDirectoryDomain')" v-model="parameter.ActiveDirectoryDomain">
                            <template v-slot:append>
                                <v-tooltip bottom>
                                    <template v-slot:activator="{ on }">
                                        <v-icon v-on="on"> mdi-help-circle-outline </v-icon>
                                    </template>
                                    adserver.local
                                </v-tooltip>
                            </template>
                        </v-text-field>
                        <v-text-field v-if="!parameter.AzureUseActiveDirectory"
                            :label="$t('ParamActiveDirectoryAllowedUserGroups')"
                            v-model="parameter.ActiveDirectoryAllowedUserGroups"></v-text-field>
                    </v-card-text>
                </v-card>
                <v-card>
                    <v-card-title>
                        <v-icon large left> mdi-microsoft-onedrive </v-icon>
                        <span class="title font-weight-light">{{ $t('OneDrive') }}</span>
                    </v-card-title>
                    <v-card-text>
                        <v-text-field :label="$t('OneDrive URL')"
                            v-model="parameter.OneDriveMicrosoftGraphApiURL"></v-text-field>
                        <v-text-field :label="$t('ClientID')"
                            v-model="parameter.OneDriveMicrosoftGraphClientID"></v-text-field>
                        <v-text-field :label="$t('ClientSecret')" v-model="parameter.OneDriveMicrosoftGraphClientSecret"
                            type="password"></v-text-field>
                    </v-card-text>
                </v-card>
                <v-card>
                    <v-card-title>
                        <v-icon large left> mdi-google-drive </v-icon>
                        <span class="title font-weight-light">{{ $t('Google Drive') }}</span>
                    </v-card-title>
                    <v-card-text>
                        <v-text-field :label="$t('Google Drive URL')"
                            v-model="parameter.GoogleDriveApiUrl"></v-text-field>
                        <v-text-field :label="$t('ClientID')"
                            v-model="parameter.GoogleDriveClientId"></v-text-field>
                        <v-text-field :label="$t('ClientSecret')" v-model="parameter.GoogleDriveClientSecret"
                            type="password"></v-text-field>
                    </v-card-text>
                </v-card>

                <!-- Email Settings -->
                <v-card>
                    <v-card-title>
                        <v-icon large left>mdi-email</v-icon>
                        <span class="title font-weight-light">{{ $t("Email Settings") }}</span>
                    </v-card-title>
                    <v-card-text>
                        <v-select v-model="emailMethod" :items="emailMethods" :label="$t('Type')"
                            item-text="text" item-value="id"></v-select>

                        <!-- SMTP Settings -->
                        <v-text-field v-show="showSmtpMethod" v-model="emailSettings.emailServer" :label="$t('Server')"
                            :error-messages="emailServerErrors" @input="$v.emailSettings.emailServer.$touch()"
                            @blur="$v.emailSettings.emailServer.$touch()" />

                        <v-text-field v-show="showSmtpMethod" v-model="emailSettings.emailPort" :label="$t('Port')"
                            max="50" min="1" step="1" type="number" :error-messages="emailPortErrors"
                            @input="$v.emailSettings.emailPort.$touch()"
                            @blur="$v.emailSettings.emailPort.$touch()"></v-text-field>

                        <!-- Email Account -->
                        <v-text-field v-model="emailSettings.emailAccount" :label="$t('Email')"
                            :error-messages="emailAccountErrors" @input="$v.emailSettings.emailAccount.$touch()"
                            @blur="$v.emailSettings.emailAccount.$touch()"></v-text-field>

                        <!-- Password for SMTP -->
                        <v-text-field v-show="showSmtpMethod" v-model="emailSettings.emailPassword"
                            :label="$t('Password')" :type="showPassword ? 'text' : 'password'"
                            :append-icon="showPassword ? 'mdi-eye' : 'mdi-eye-off'"
                            @click:append="showPassword = !showPassword" :error-messages="emailPasswordErrors"
                            @input="$v.emailSettings.emailPassword.$touch()"
                            @blur="$v.emailSettings.emailPassword.$touch()"></v-text-field>
                        <v-switch v-show="showSmtpMethod" color="green" v-model="emailSettings.emailUseSSL"
                            :label="$t('SSL')"></v-switch>

                        <!-- API Key for SendGrid -->
                        <v-text-field v-show="!showSmtpMethod" v-model="emailSettings.sendGridApiKey"
                            :label="$t('API KEY')" :error-messages="sendGridApiKeyErrors"
                            @input="$v.emailSettings.sendGridApiKey.$touch()"
                            @blur="$v.emailSettings.sendGridApiKey.$touch()"></v-text-field>

                        <!-- Test Button -->
                        <v-row align="center" class="mx-2">
                            <v-btn color="warning" @click="test">{{ $t('Test') }}</v-btn>
                            <v-spacer />
                        </v-row>
                    </v-card-text>
                </v-card>
            </v-col>
        </v-row>
        <v-row dense v-if="!loading">
            <v-col md="6" sm="12">
                <v-card>
                    <v-card-title>
                        <v-icon large left> mdi-security </v-icon>
                        <v-switch v-model="parameter.SAMLEnabled" color="green" :label="$t('SSO')"></v-switch>
                    </v-card-title>
                    <v-card-text v-if="parameter.SAMLEnabled">
                        <v-text-field :label="$t('ParamSAMLEndpoint')" v-model="parameter.SAMLEndpoint"></v-text-field>
                        <v-textarea v-model="parameter.SAMLCertificate"
                            :label="$t('ParamSAMLCertificate')"></v-textarea>
                    </v-card-text>
                </v-card>
            </v-col>
            <v-col>
                <v-card>
                    <v-card-title>
                        <v-icon large left> mdi-draw </v-icon>
                        <span class="title font-weight-light">{{ $t("QuoVadis V2") }}</span>
                    </v-card-title>
                    <v-card-text>
                        <v-text-field :label="$t('ClientID')" v-model="parameter.ClientID"></v-text-field>
                        <v-text-field :label="$t('CredentialID')" v-model="parameter.CredentialID"></v-text-field>
                        <v-text-field :label="$t('ClientSecret')" v-model="parameter.ClientSecret"
                            :append-icon="showSecret ? 'mdi-eye' : 'mdi-eye-off'"
                            @click:append="showSecret = !showSecret"
                            :type="showSecret ? 'text' : 'password'"></v-text-field>
                        <v-text-field :label="$t('UserID')" v-model="parameter.UserID"></v-text-field>
                        <v-text-field :label="$t('ApiURL')" v-model="parameter.QuoVadisV2ApiURL"></v-text-field>
                        <v-row class="pl-3">
                            <v-btn small color="primary" @click="generateSAD">
                                {{ $t("GenerateSAD") }}
                                <v-icon>mdi-account-credit-card-outline</v-icon>
                            </v-btn>
                            <span class="title font-weight-light">{{ $t("ExpiresOn") }}: {{ expirationSAD }}</span>
                            <h1></h1>
                        </v-row>
                    </v-card-text>
                </v-card>
            </v-col>
        </v-row>
        <v-fab-transition>
            <v-tooltip left>
                <template v-slot:activator="{ on }">
                    <v-btn color="primary" v-on="on" dark absolute bottom right fab :loading="loadingSave" class="mb-10"
                        @click="save">
                        <v-icon large>mdi-content-save</v-icon>
                    </v-btn>
                </template>
                <span>{{ $t("Save") }}</span>
            </v-tooltip>
        </v-fab-transition>
    </v-container>
</template>

<script>
import ParameterService from '@/services/ParameterService';
import EmailSettingService from '@/services/EmailSettingService';
import EmailSettings from '@/ecm/models/EmailSettings';
import OrganizationService from '@/services/OrganizationService';
import Parameter from '@/ecm/models/Parameter';
import Utils from '@/ecm/common/Utils';
import { required, requiredIf, email, } from 'vuelidate/lib/validators';
import { validationMixin } from 'vuelidate';
import SecurityRelevantFiles from '../../ecm/variables/SecurityRelevantFiles';
import EmailProviders from '@/ecm/variables/EmailProviders';

export default {
    mounted() {
        this.getParameters();
        this.getOrganizations();
    },
    computed: {
        showSmtpMethod() {
            return this.emailMethod == 0;
        },
        emailAccountErrors() {
            const errors = [];
            if (!this.$v.emailSettings.emailAccount.$dirty) return errors;
            !this.$v.emailSettings.emailAccount.required && errors.push(this.$t('RequiredField'));
            !this.$v.emailSettings.emailAccount.email && errors.push(this.$t('InvalidEmail'));
            return errors;
        },
        emailServerErrors() {
            const errors = [];
            if (!this.$v.emailSettings.emailServer.$dirty) return errors;
            !this.$v.emailSettings.emailServer.required && errors.push(this.$t('RequiredField'));
            return errors;
        },
        emailPortErrors() {
            const errors = [];
            if (!this.$v.emailSettings.emailPort.$dirty) return errors;
            !this.$v.emailSettings.emailPort.required && errors.push(this.$t('RequiredField'));
            return errors;
        },
        emailPasswordErrors() {
            const errors = [];
            if (!this.$v.emailSettings.emailPassword.$dirty) return errors;
            !this.$v.emailSettings.emailPassword.required && errors.push(this.$t('RequiredField'));
            return errors;
        },
        sendGridApiKeyErrors() {
            const errors = [];
            if (!this.$v.emailSettings.sendGridApiKey.$dirty) return errors;
            !this.$v.emailSettings.sendGridApiKey.required && errors.push(this.$t('RequiredField'));
            return errors;
        }
    },
    data() {
        return {
            loading: false,
            loadingSave: false,
            parameter: new Parameter(),
            emailSettings: new EmailSettings(),
            cacheChanged: false,
            expirationSAD: '',
            showSecret: false,
            organizations: [],
            emailMethod: 0,
            showPassword: false,
            emailMethods: [
                { id: 0, text: 'SMTP' },
                { id: 1, text: 'SendGrid' },
            ],
            selectedModel: '',

        };
    },
    mixins: [validationMixin],
    validations: {
        emailSettings: {
            emailAccount: { required, email },
            emailServer: {
                required: requiredIf(function () {
                    return this.emailMethod == 0;
                }),
            },
            emailPort: {
                required: requiredIf(function () {
                    return this.emailMethod == 0;
                }),
            },
            emailPassword: {
                required: requiredIf(function () {
                    return this.emailMethod == 0;
                }),
            },
            sendGridApiKey: {
                required: requiredIf(function () {
                    return this.emailMethod == 1;
                }),
            },
            twoFactorAuthenticationServiceId: {
                required: requiredIf(function () {
                    return this.emailSettings.twoFactorAuthenticationAuthApiKey;
                }),
            },
            twoFactorAuthenticationAuthApiKey: {
                required: requiredIf(function () {
                    return this.emailSettings.twoFactorAuthenticationServiceId;
                }),
            },
        }
    },
    methods: {
        getForbiddenExtensions() {
            if (!this.parameter.SupportedFileFormats) {
                return [];
            }

            const extensions = this.parameter.SupportedFileFormats
                .split(';')
                .map(ext => ext.trim())
                .filter(ext => ext.length > 0)
                .map(ext => ext.toLowerCase());

            return extensions.filter(ext =>
                SecurityRelevantFiles.NotAllowedExtensionList.includes(ext)
            );
        },
        test() {
            this.$v.$touch();
            if (this.$v.$invalid) {
                return;
            }
            this.loading = true;
            OrganizationService.testEmailConfig(this.emailSettings)
                .then(() => {
                    Utils.toast.success(this.$t('EmailSent'));
                })
                .catch(() => { console.error('test error'); })
                .finally(() => {
                    //always
                    this.loading = false;
                });
        },
        resetCache() {
            OrganizationService.clearDashboardCache();
        },
        getOrganizations() {
            this.loading = true;
            OrganizationService.getList().then((data) => {
                this.organizations = data;
                this.organizations = this.organizations.filter((org) => org.id !== '1');
                this.loading = false;
            });
        },
        getParameters() {
            this.loading = true;
            ParameterService.getAll().then((data) => {
                EmailSettingService.getAll().then((responseEmailSettings) => {
                    this.emailSettings = responseEmailSettings;
                });
                this.emailMethod = this.emailSettings.sendGridApiKey ? 1 : 0;
                this.parameter = new Parameter(data);
                this.getSADExpiration();
            });
            this.loading = false;
        },
        generateSAD() {
            this.$confirm(this.$t('GenerateSADConfirmation'), {
                title: this.$t('GenerateSAD'),
                icon: 'mdi-alert',
                color: 'orange',
                buttonTrueText: this.$t('Accept'),
                buttonFalseText: this.$t('Cancel'),
            }).then((res) => {
                if (res == 1) {
                    this.loadingSave = true;
                    ParameterService.save(this.parameter.toDto()).then((data) => {
                        ParameterService.generateSAD().then((data2) => {
                            const message = 'SAD ' + this.$t('CreatedSuccessfully');
                            Utils.toast.success(message);
                            this.getSADExpiration();
                            this.getParameters();
                            this.loadingSave = false;
                        });
                    });
                }
            });
        },
        async save() {
            this.$v.$touch();
            if (this.$v.$invalid) {
                return;
            }
            this.loadingSave = true;

            try {
                if (this.emailMethod == EmailProviders.SMTP) {
                    this.emailSettings.sendGridApiKey = '';
                    this.emailSettings.changedPassword = this.emailSettings.emailPassword != '%%ACCOUNT_PASSWORD%%';
                } else {
                    this.emailSettings.emailServer = '';
                    this.emailSettings.emailPassword = '';
                    this.emailSettings.emailPort = 0;
                    this.emailSettings.changedPassword = false;
                }

                const forbiddenExtensions = this.getForbiddenExtensions();
                if (forbiddenExtensions.length > 0) {
                    Utils.toast.error(
                        this.$t('Site.ContentType.NotAllowedExtensions') + ': ' + forbiddenExtensions.join(', ')
                    );
                    return;
                }

                await ParameterService.save(this.parameter.toDto());
                await ParameterService.clearCache();
                await EmailSettingService.save(this.emailSettings);

                Utils.toast.success(this.$t('Parameters') + ' ' + this.$t('SavedSuccessfully'));
                this.getParameters();
            } catch (error) {
                console.error('Error saving parameters:', error);
                Utils.toast.error(this.$t('Error saving parameters.'));
            } finally {
                this.loadingSave = false;
            }
        },

        getSADExpiration() {
            const userDateFormat = this.$store.getters['account/user'].MetricDateFormat;
            ParameterService.getSADExpiration().then((data) => {
                var expirationDate = new Date(data);
                var today = new Date();
                if (expirationDate > today) {
                    this.expirationSAD = Utils.date.toUserFormat(
                        expirationDate,
                        userDateFormat
                    );
                } else {
                    this.expirationSAD = this.$t('Expired');
                }
            });
        },
    },
};
</script>
